import React, { useEffect, useState } from 'react'
import { withFormik } from 'formik'
import * as Yup from 'yup'
import './CancellationForm.css'
import { useNavigate } from 'react-router-dom'
import { PageProperties } from '../../types'
import { pb } from '../../api-utils'
import { RecordModel } from 'pocketbase';
import { separator } from '../../App'
import { formatDate } from '../../utils'

const EMOJI_CHECK_MARK = "✅";

interface CancellationFormValues {
  move_out_date: undefined | Date,
  move_out_reason: string,
  additional_comment: string,
}

const PLEASE_WAIT = "Please wait ...";
const THANK_YOU_TITLE = `${EMOJI_CHECK_MARK} Cancellation Request Submitted!`;
const THANK_YOU_SUBTITLE = "We will process your cancellation e-mail you a confirmation";

const CancellationForm = ({ page }: PageProperties) => {
  const queryStr = window.location.search
  const urlParams = new URLSearchParams(queryStr)
  const spaceId = urlParams.get('id');
  const formSubmitted = urlParams.get('formSubmitted') === "true";

  const [space, setSpace] = useState<any | undefined>();
  const [customer, setCustomer] = useState<any | undefined>();
  const [lease, setLease] = useState<any | undefined>();
  const [dbAdminToken, setDbAdminToken] = useState<string | undefined>();
  const [pageTitle, setPageTitle] = useState<string | undefined>();
  const [pageSubtitle, setPageSubtitle] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const navigate = useNavigate();

  // set oauthToken
  useEffect(() => {
    if (spaceId && !space) {
      // THIS NEEDS TO BE FIXED AND MOVED TO NETLIFY
      pb.collection('api_clients').authWithPassword(import.meta.env.VITE_DB_ADMIN_EMAIL!, import.meta.env.VITE_DB_ADMIN_PASSWORD!, {
        autoRefreshThreshold: 30 * 60
      }).then((adminAuthData: any) => {
        setDbAdminToken(adminAuthData.token);
      }).catch((err: any) => {
        console.error("Error authenticating admin:", err);
        setError("Unable to access storage information. Please contact support.");
        setIsLoading(false);
      });
    }
  }, [dbAdminToken, space, spaceId]);

  // set Parking Space item
  useEffect(() => {
    if (dbAdminToken && spaceId && !space) {
      pb.collection('spaces').getOne(spaceId)
        .then((data: RecordModel) => {
          console.log(">>DB GOT space ", data)
          setSpace(data);
          if (!data.customer) {
            setError("This storage space is not currently rented. No cancellation is needed.");
            setIsLoading(false);
          }
        })
        .catch((err: any) => {
          console.error("Error fetching space:", err);
          setError("Storage space not found. Please check the URL and try again.");
          setIsLoading(false);
        });
    }
  }, [dbAdminToken, spaceId, space]);

  // Get customer and lease information
  useEffect(() => {
    if (space && space.customer && !customer) {
      pb.collection('customers').getOne(space.customer)
        .then((data: RecordModel) => setCustomer(data))
        .catch((err: any) => {
          console.error("Error fetching customer:", err);
          setError("Unable to load customer information. Please contact support.");
          setIsLoading(false);
        });
    }
  }, [space, customer]);

  useEffect(() => {
    if (space && space.customer && !lease) {
      pb.collection('leases').getFirstListItem(`customer = "${space.customer}" && spaces ?~ "${spaceId}"`)
        .then((data: RecordModel) => {
          setLease(data);
          setIsLoading(false);
        })
        .catch((err: any) => {
          console.error("Error fetching lease:", err);
          setError("Unable to load lease information. Please contact support.");
          setIsLoading(false);
        });
    }
  }, [space, spaceId, lease]);

  const updateLeaseWithCancellation = async (values: CancellationFormValues) => {
    if (lease) {
      const now = new Date();
      const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));

      // Combine reason and additional comments for the termination_reason field
      const fullReason = values.additional_comment
        ? `${values.move_out_reason}\n\nAdditional Comments: ${values.additional_comment}`
        : values.move_out_reason;

      await pb.collection('leases').update(lease.id, {
        move_out_date: values.move_out_date,
        termination_reason: fullReason,
        termination_date: easternTime,
        status: 'terminated'
      });

      // Update space status to available
      await pb.collection('spaces').update(spaceId!, {
        status: 'vacant',
        customer: null,
        move_in_date: null,
        vehicle: null
      });
    }
  }

  const innerForm = ({
    values,
    touched,
    errors,
    dirty,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleReset,
    isValid,
    setFieldTouched
  }: any) => {

    // Function to handle real-time validation by touching fields
    const handleFieldChange = (e: any) => {
      handleChange(e);
      // Touch the field to show validation errors immediately
      setFieldTouched(e.target.name || e.target.id, true, false);
    };

    return (
      <form onSubmit={handleSubmit} className="cancellation-form">
        {customer && (
          <div className="allInputs">
            <div className="column is-full">
              <div className="cancellation-info">
                <strong>Customer Information:</strong><br />
                {customer.first_name} {customer.last_name}<br />
                {customer.email}<br />
                {customer.phone}
              </div>
            </div>
          </div>
        )}

        {space && (
          <div className="allInputs" style={{ marginTop: "-2rem" }}>
            <div className="column is-full">
              <div className="cancellation-info">
                <strong>Storage Space:</strong><br />
                {space.length}ft {space.type} - ${space.monthly_rate}/month
              </div>
            </div>
          </div>
        )}

        {separator}

        <div className="allInputs">
          <div className="column is-one-third">
            <label className="forceBlack" >Move-Out Date<span style={{ fontSize: 14 }}> *</span></label>
            <input
              id="move_out_date"
              name="move_out_date"
              style={{ width: 180, backgroundColor: 'white' }}
              type="date"
              min={new Date().toISOString().split('T')[0]}
              value={formatDate(values.move_out_date) || undefined}
              onChange={handleFieldChange}
              onBlur={handleBlur}
              required
            />
            {errors.move_out_date && touched.move_out_date && <div className="input-feedback">{errors.move_out_date}</div>}
          </div>
        </div>

        <div className="allInputs">
          <div className="column is-two-thirds">
            <label htmlFor="move_out_reason" className="forceBlack" >
              Reason for Cancellation<span style={{ fontSize: 14 }}> *</span>
            </label>
            <textarea
              id="move_out_reason"
              name="move_out_reason"
              placeholder="Please provide the reason for cancelling your storage lease"
              style={{ width: '100%', minHeight: 80, padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
              value={values.move_out_reason}
              onChange={handleFieldChange}
              onBlur={handleBlur}
              className={errors.move_out_reason && touched.move_out_reason ? 'text-input error' : 'text-input'}
              required
            />
            {errors.move_out_reason && touched.move_out_reason && <div className="input-feedback">{errors.move_out_reason}</div>}
          </div>
        </div>

        <div className="allInputs">
          <div className="column is-two-thirds">
            <label htmlFor="additional_comment" className="forceBlack" >
              Additional Comments<span style={{ fontSize: 14 }}> (optional)</span>
            </label>
            <textarea
              id="additional_comment"
              name="additional_comment"
              placeholder="Any additional comments or information you'd like to provide"
              style={{ width: '100%', minHeight: 80, padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
              value={values.additional_comment}
              onChange={handleFieldChange}
              onBlur={handleBlur}
            />
          </div>
        </div>

        <div className="allInputs">
          <div className="column is-two-thirds">
            <div className="notes-section">
              <div style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'start' }}>
                <div style={{ marginBottom: 10 }}>Please note:</div>
                <ul style={{ marginLeft: 20 }}>
                  <li>30-day notice is required for lease cancellation</li>
                  <li>Security deposit will be refunded after move-out inspection</li>
                  <li>All personal items must be removed by the move-out date</li>
                  <li>You will receive confirmation of your cancellation request</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {separator}

        <button
          type="reset"
          id="reset"
          name="reset"
          className="outline"
          onClick={handleReset}
          disabled={!dirty || isSubmitting}
          style={{ background: !dirty || isSubmitting ? 'grey' : 'transparent', color: '#232043', border: '2px solid #232043' }}
        >
          <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Reset</span>
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !dirty || !isValid}
          style={{ background: '#232043', color: 'yellow' }}
        >
          <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Submit</span>
        </button>
      </form>
    )
  }

  const EnhancedForm = withFormik({
    mapPropsToValues: () => ({
      move_out_date: undefined,
      move_out_reason: '',
      additional_comment: '',
    }),

    validationSchema: Yup.object().shape({
      move_out_date: Yup.date().required('*Move-out date is required').min(new Date(), 'Move-out date must be in the future'),
      move_out_reason: Yup.string().required('*Reason for cancellation is required').min(10, 'Please provide a more detailed reason'),
    }),

    handleSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      setPageSubtitle("... processing your cancellation request, please wait")
      setTimeout(async () => {
        try {
          await updateLeaseWithCancellation(values);
          setPageTitle(THANK_YOU_TITLE);
          setPageSubtitle(THANK_YOU_SUBTITLE);
          navigate(`/cancellation-form?id=${spaceId}&formSubmitted=true`)
        } catch (error: any) {
          console.error("Error submitting cancellation:", error);
          setPageSubtitle("An error occurred. Please try again or contact us directly.");
        }
        setSubmitting(false)
      }, 1000)
    },
    displayName: 'CancellationForm',
  })(innerForm)

  console.log("CANCELLATION FORM: ", {
    space,
    dbAdminToken,
    formSubmitted,
    isLoading,
    error
  })

  return (
    <div style={{ minHeight: "64vh", background: `linear-gradient(${page.gradient?.direction}, ${page.gradient?.first}, ${page.gradient?.second})` }}>
      <div className="view column"
        style={{
          padding: "8%",

        }}>
        <div className={`title ${page.textColor ? "" : "forceWhite"}`}>
          {pageTitle || (!formSubmitted && page.title) || PLEASE_WAIT}
        </div>
        <div className="forceBlack subtitle">
          {pageSubtitle || (!formSubmitted && page.subtitle)}
          {separator}
        </div>
        {
          isLoading && !formSubmitted && (
            <div className="forceBlack" style={{ textAlign: 'center', padding: '20px' }}>
              Loading storage information...
            </div>
          )
        }
        {
          error && !formSubmitted && (
            <div className="forceBlack" style={{ textAlign: 'center', padding: '20px', color: '#dc3545' }}>
              {error}
            </div>
          )
        }
        {
          !formSubmitted && !isLoading && !error && (
            <EnhancedForm />
          )
        }
      </div>
    </div>
  )
}

export default CancellationForm 